import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import styles from './ErrorBoundary.module.scss';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. If not provided, uses the default error page. */
  fallback?: ReactNode;
  /** Override the default error title (class components cannot use hooks, so accept as prop) */
  title?: string;
  /** Override the default error message */
  message?: string;
  /** Override the retry button label */
  retryLabel?: string;
  /** Override the home button label */
  homeLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in development — replace with error tracking service (e.g., Sentry) in production
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.icon}>!</div>
            <h2 className={styles.title}>{this.props.title ?? 'Something went wrong'}</h2>
            <p className={styles.message}>
              {this.props.message ?? 'An unexpected error occurred. Please try again or navigate to a different page.'}
            </p>
            {this.state.error && (
              <details className={styles.details}>
                <summary className={styles.detailsSummary}>Error details</summary>
                <pre className={styles.errorText}>{this.state.error.message}</pre>
              </details>
            )}
            <div className={styles.actions}>
              <button className={styles.retryBtn} onClick={this.handleReset}>
                {this.props.retryLabel ?? 'Try Again'}
              </button>
              <button className={styles.homeBtn} onClick={() => { window.location.href = '/'; }}>
                {this.props.homeLabel ?? 'Go to Home'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
